import { useStore } from 'zustand';

import { kindOf } from '@/domain/model';
import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';
import { KIND_NAMES } from './kindNames';
import { colour } from './theme';

/**
 * The name of the one thing that is selected.
 *
 * ## The gap this closes
 *
 * There was no way to name something from the canvas. `label A "A"` names it in the script, and
 * the objects list renames a row on a double-click — and that is the whole list. Double-clicking
 * the object itself does nothing: `requestTextEdit` refuses anything that is not a `text` entity,
 * so the gesture a user reaches for first is the one that silently does nothing.
 *
 * What that costs is worst in the commonest case. A teacher draws three points with the mouse and
 * wants them called A, B and C. Today: open **Đối tượng**, find three rows that all read "điểm",
 * click one to see which lit up, double-click it, type, and repeat. The naming of a figure is
 * most of the work of drawing it — every problem opens by naming corners — and it was the one
 * thing the canvas could not do.
 *
 * ## Why it names anything, not only a point
 *
 * The same gap swallows every other kind. A segment carries a length caption, a circle is `(O)`, a
 * plane in a solid figure is `(SAB)` — the reference tells a writer to say exactly that, and the
 * mouse could not. One field for the selection is also one rule to learn rather than a special
 * case per kind.
 *
 * ## Why a single selection only
 *
 * Because naming four things the same thing is not a thing anybody wants, and a field that
 * silently applied to all of them would be a way to destroy four names with one keystroke. The
 * other contextual panels take the same line for the same reason.
 *
 * ## Why there is no draft
 *
 * The first cut held what was typed and applied it on Enter or on blur, which is what a numeric
 * field in this rail does — and it lost the name in the commonest way of finishing. Reported as
 * "đặt tên cho điểm thì nó chưa lưu": a person types `A` and then clicks the drawing. A canvas
 * takes no focus, so there is no blur; and if the click lands on something else the selection
 * changes, this panel re-renders for that object, and the input unmounts carrying the name away
 * with it. Nothing said so, because from the code's point of view nothing had happened.
 *
 * A number needs a draft — `-` and `1.` are states of typing `-1.5` and neither is a number, so
 * committing them would fight the typist. A name has no such states: every prefix of `A'` is a
 * name somebody might mean. So each keystroke is applied, the field always shows the truth, and
 * there is nothing to lose. The undo history collapses the run — see `setPresentation` — so
 * typing three letters is one step back, not three.
 *
 * ## Why a name already in use is a warning and not a refusal
 *
 * Two points called A is a figure that lies: a reader cannot tell which one the problem means,
 * and nothing in the drawing gives it away. The script has always refused a duplicate *variable*
 * name; the drawn label had no check at all.
 *
 * But refusing it outright would be wrong, because it is sometimes exactly right. Two exercises
 * side by side on one page each have their own triangle ABC, and a teacher laying out a worksheet
 * is not making a mistake. Refusal is for what cannot be built — a solid on coplanar points, a
 * plane through corners of no body. This can be built and is usually not meant, which is what a
 * warning is for: it is said, once, where the name is being typed, and the decision stays with
 * the person who can see the page.
 *
 * ## Why a `text` object is left out
 *
 * Its words are its *content*, not its name — `source.content`, edited by double-clicking it,
 * which is the one place that gesture already works. A `label` on top of that would be a name for
 * a name, drawn beside itself.
 */
export function NamePanel({ store }: { readonly store: EditorStore }) {
  const document = useStore(store, (state) => state.document);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  const chosen = [...selection];
  const only = chosen.length === 1 ? chosen[0] : undefined;
  const entity = only === undefined ? undefined : document.entities.get(only);

  if (entity === undefined || entity.source.op === 'text') return null;

  const current = entity.label ?? '';

  const clash =
    current === ''
      ? undefined
      : [...document.entities.values()].find(
          (other) => other.id !== entity.id && other.label === current
        );

  return (
    <PanelSection title="Tên" badge={KIND_NAMES[kindOf(entity.source)]}>
      <input
        className="es-field"
        value={current}
        placeholder="chưa đặt tên"
        onChange={(event) => {
          const typed = event.target.value;
          commands.setPresentation([entity.id], {
            label: typed.trim() === '' ? null : typed,
          });
        }}
        onKeyDown={(event) => {
          /*
           * Enter commits and Escape abandons, and both stop there. The canvas listens for keys —
           * Delete removes the selection — and a name being typed must not reach it: `Escape` in
           * particular would otherwise cancel the tool underneath while the user thought they were
           * cancelling their own edit.
           */
          event.stopPropagation();
          // Nothing to commit — the name is already applied. Both keys simply put the field away,
          // and Escape leaves what was typed, because undo is where taking it back belongs.
          if (event.key === 'Enter' || event.key === 'Escape') event.currentTarget.blur();
        }}
        style={{ fontSize: 13, padding: '6px 8px' }}
      />
      {clash !== undefined && (
        <p
          role="alert"
          style={{ margin: 0, color: 'var(--es-danger)', fontSize: 10, lineHeight: 1.45 }}
        >
          Đã có một {KIND_NAMES[kindOf(clash.source)]} cũng tên {current}. Trên cùng một hình thì
          người đọc không biết đề đang nói cái nào.
        </p>
      )}
      <p style={{ margin: 0, color: colour.faint, fontSize: 10, lineHeight: 1.45 }}>
        Viết được ký hiệu toán: <code>A_1</code>, <code>{"A'"}</code>, <code>\alpha</code>,{' '}
        <code>(SAB)</code>. Xoá trắng để bỏ tên.
      </p>
    </PanelSection>
  );
}
